/**
 * VoiceInput 组件
 * 语音/文本模式切换按钮
 */
import React, { memo, useCallback } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { voiceModeAtom } from '../../../../atoms/v3/chatInputAtoms';
import { isDarkAtom } from '../../../../atoms/shared/uiAtoms';
import { isTouchDevice } from '../../../../utils/platformUtils';

interface VoiceInputProps {
  isDisabled?: boolean;
  className?: string;
  onClick?: () => void; // quickbar 模式下的点击回调
}

const VoiceInputComponent: React.FC<VoiceInputProps> = ({
  isDisabled = false,
  className = "",
  onClick
}) => {
  const [voiceMode, setVoiceMode] = useAtom(voiceModeAtom);
  const isDark = useAtomValue(isDarkAtom);
  const isTouch = isTouchDevice();

  // 处理模式切换 - 使用useCallback优化
  const handleModeToggle = useCallback(() => {
    // 如果有外部 onClick 回调（quickbar 模式），优先执行
    if (onClick) {
      onClick();
      return;
    }
    setVoiceMode(!voiceMode);
  }, [voiceMode, setVoiceMode, onClick]);

  return (
    <button
      onClick={handleModeToggle}
      disabled={isDisabled}
      className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
        isDark
          ? 'bg-ios-dark-gray4 hover:bg-ios-dark-gray3'
          : 'bg-gray-100 hover:bg-gray-200'
      } ${className}`}
      aria-label={voiceMode ? "切换到文本模式" : "切换到语音模式"}
      title={voiceMode ? "切换到文本模式" : "切换到语音模式"}
    >
      {voiceMode ? (
        // 文本模式图标 - 显示键盘/文本图标
        <svg
          className={`w-5 h-5 ${isDark ? 'text-ios-dark-gray1' : 'text-gray-600'}`}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M3 7C3 5.89543 3.89543 5 5 5H19C20.1046 5 21 5.89543 21 7V17C21 18.1046 20.1046 19 19 19H5C3.89543 19 3 18.1046 3 17V7Z" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M7 9H7.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <path d="M11 9H11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <path d="M15 9H15.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <path d="M7 13H7.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <path d="M11 13H11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <path d="M15 13H15.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <path d="M9 17H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      ) : (
        // 语音模式图标 - 显示麦克风图标，使用与 QuickActionBar 相同的样式
        <svg
          className={`w-5 h-5 ${isDark ? 'text-ios-dark-gray1' : 'text-gray-600'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
        </svg>
      )}
    </button>
  );
};

// 使用memo包装组件以避免不必要的重渲染
export const VoiceInput = memo(VoiceInputComponent);
export default VoiceInput;
